import { INSTRUMENT_HIERARCHY } from '../data/InstrumentRegistry.js';
import { applyInstrumentVoicing, constrainToRange } from './OrchestralLogic/VoicingEngine.js';
import { filterTimpaniPart, filterPercussionByTension } from './OrchestralLogic/PercussionLogic.js';
import { generateStringOstinato, generateBrassStabs, generatePolyrhythm, generateAtmosphericPad, generateBrokenChord, generateOrchestralSwell } from './OrchestralLogic/TextureEngine.js';
import { applyGuitarVoicing, applyStrum } from './StrummingEngine.js';
import { applyPianoPattern } from './OrchestralLogic/PianoLogic.js';
import { ParameterResolver } from './ParameterResolver.js';
import { generateEuclideanRhythm, patternToEvents, getProceduralPhraseType, getPhrasePattern, getBeatStrength } from './RhythmLogic.js';
import { RHYTHM_PATTERNS, getRandomPattern } from '../data/RhythmLibrary.js';
import { INFLUENCE_WEIGHTS } from './constants.js';
import { Random } from './Random.js';
import { ORCHESTRATION_WEIGHTS } from '../data/StrategyConfig.js';

/**
 * RoleAllocator.js
 * The "Brain" of Dynamic Orchestration.
 * Assigns abstract musical roles (Bass, Melody, Harmony) to the best available instruments via Capability Scoring.
 */

export const ABSTRACT_ROLES = {
    MELODY: 'melody',
    HARMONY: 'harmony', // Basic Harmony Role
    HARMONY_PAD: 'harmony_pad', // Voice A: Sustain/Bed
    HARMONY_RHYTHMIC: 'harmony_rhythmic', // Voice B: Comping/Stabs
    HARMONY_COLOR: 'harmony_color', // Voice C: Arps/Extensions
    BASS: 'bass',
    COUNTER_MELODY: 'counter_melody',
    PERC_LOW: 'perc_low',
    PERC_MID: 'perc_mid',
    PERC_HIGH: 'perc_high'
};

// Helper to safely get range
function getSafeRange(instrument) {
   if (!instrument) return { min: 0, max: 127 };
   if (Array.isArray(instrument.range) && instrument.range.length >= 2) {
       return { min: instrument.range[0], max: instrument.range[1] };
   }
   if (instrument.range && typeof instrument.range.min === 'number') return instrument.range;
   return { min: 36, max: 84 };
}

// Task 2: "Violin Gravity" (Bell Curve Register)
const applyGoldenZoneBias = (note, instrument, tension) => {
    // Only apply to High Strings (Violin 1/2) or High Woodwinds
    if (!instrument.id.includes('violin') && !instrument.id.includes('flute')) return note;

    // Fix: Force High Register (> 72) for Cinematic/Romantic
    // If note is in Mid range (60-72), push it up to ensure we hit the High bin.
    // Reduced probability to 30% to prevent High Frequency Saturation (Mid range starvation).
    if (note >= 60 && note <= 72) {
        if (Math.random() > 0.7) return note + 12; // Was 0.2 (80%) -> Now 30%
    }
    // Also capture lower-mid (55-60) and bump them too
    if (note >= 55 && note < 60) {
        if (Math.random() > 0.5) return note + 12; // Was 0.1 (90%) -> Now 50%
    }

    // Golden Zone: G4 (67) to C6 (84). Center approx 76.
    // If tension is extremely low (Intro), allow high ethereal notes (don't clamp)
    if (tension < 0.2) return note;

    // If tension is high (> 0.8), allow upper register freedom (Climax)
    if (tension > 0.8) return note;

    // Calculate distance from Golden Zone
    // We want to pull extreme high notes down, unless they are intentional climaxes
    if (note > 84) {
        // Bias towards dropping an octave
        // Probability increases as note gets higher
        const prob = Math.min(1.0, (note - 84) / 12); // Linear ramp
        if (Math.random() < prob) {
            return note - 12;
        }
    }
    return note;
};

/**
 * Calculates a score (0.0 - 1.0) indicating how well an instrument fits a role.
 * @param {Object} instrument
 * @param {string} role
 * @param {string} sectionType
 * @param {Object} config - (Optional) Dynamic configuration
 * @returns {number}
 */
const calculateCapabilityScore = (instrument, role, sectionType = 'VERSE', config = {}) => {
    let score = ORCHESTRATION_WEIGHTS.baseScore;
    const traits = instrument.capabilities?.traits || {};
    const declaredRoles = instrument.roles || [];

    // Phase 4.3: Dynamic Vector Influence
    // Use harmonicComplexity to bias role selection
    const complexity = (config && config.vectors) ? config.vectors.harmonicComplexity : 0.5;

    // Task 2: Liquid Orchestra (Section-Based Bias)
    if (role === ABSTRACT_ROLES.MELODY && sectionType) {
        const type = sectionType.toUpperCase();
        const id = instrument.id.toLowerCase();
        const family = (instrument.traits && instrument.traits.family) ? instrument.traits.family.toLowerCase() : '';

        // INTRO: Piano, Harp, Solo Woodwinds
        if (type.includes('INTRO') || type.includes('BREAK')) {
            if (id.includes('piano') || id.includes('harp')) score += 0.3;
            if (family === 'woodwinds' && instrument.type === 'monophonic') score += 0.2;
            if (id.includes('violin') || family === 'brass') score -= 0.1; // Reduce intensity
        }
        // BUILD: Woodwinds, Strings (Tremolo?)
        else if (type.includes('BUILD') || type.includes('RISE')) {
            if (family === 'woodwinds') score += 0.3;
            if (id.includes('cello')) score += 0.2;
        }
        // CLIMAX: Brass, Violins, Synth Lead
        else if (type.includes('CLIMAX') || type.includes('DROP') || type.includes('CHORUS')) {
            if (family === 'brass') score += 0.3;
            if (id.includes('violin') || id.includes('strings')) score += 0.25;
            if (id.includes('synth') && id.includes('lead')) score += 0.3;
            if (id.includes('piano') || id.includes('harp')) score -= 0.1; // Buried in mix
        }
    }

    // 1. Hard Constraints (Must declare role capability)
    // Map abstract roles to declared roles
    let requiredRole = role;
    if (role === ABSTRACT_ROLES.MELODY) requiredRole = 'melody';
    else if (role === ABSTRACT_ROLES.BASS) requiredRole = 'bass';
    else if (role === ABSTRACT_ROLES.HARMONY || role.startsWith('harmony_')) requiredRole = 'harmony';
    else if (role.includes('perc')) requiredRole = 'percussion';
    else if (role === ABSTRACT_ROLES.COUNTER_MELODY) requiredRole = 'counter_melody';

    // If instrument doesn't list the role, huge penalty (but not 0, emergent behavior might allow weird choices?)
    // No, keep it strict for now to avoid chaos.
    if (!declaredRoles.includes(requiredRole) && !declaredRoles.includes('universal')) {
        // Soften for 'melody' if it has 'harmony' (e.g. Piano can do Melody even if not explicitly Lead)
        if (requiredRole === 'melody' && declaredRoles.includes('harmony')) {
            score -= ORCHESTRATION_WEIGHTS.penalties.melodyIfHarmony;
        } else if (requiredRole === 'counter_melody' && declaredRoles.includes('melody')) {
            score -= ORCHESTRATION_WEIGHTS.penalties.counterIfMelody;
        } else {
            return ORCHESTRATION_WEIGHTS.penalties.missingRole;
        }
    }

    // 2. Trait Scoring
    // Generic Behavioral Bonus via Config (Data-Driven)
    const behaviors = config.traits?.roleBehaviors || {};
    const behavior = behaviors[role];
    if (behavior && behavior.scoreBonus && behavior.requiredFamily) {
        if (traits.family === behavior.requiredFamily) {
            score += behavior.scoreBonus;
        }
    }

    if (role === ABSTRACT_ROLES.BASS) {
        if (traits.spectral_role === 'low') score += ORCHESTRATION_WEIGHTS.bonuses.bass.lowSpectral;
        if (traits.sustain === 'long') score += ORCHESTRATION_WEIGHTS.bonuses.bass.longSustain;
        // Fix: Boost Bass Score significantly for 'Epic' genres to ensure allocation priority
        if (config.harmonySource === 'epic_cinematic') score += 0.3;
    }
    else if (role === ABSTRACT_ROLES.MELODY) {
        if (traits.spectral_role === 'high' || traits.spectral_role === 'mid') score += ORCHESTRATION_WEIGHTS.bonuses.melody.highMidSpectral;
        if (traits.timbre === 'bright') score += ORCHESTRATION_WEIGHTS.bonuses.melody.brightTimbre;

        // Dynamic: If complexity is high, prefer polyphonic leads? Or stick to mono?
        // Let's say high complexity allows weirder timbre choices.
        if (instrument.type === 'monophonic') score += ORCHESTRATION_WEIGHTS.bonuses.melody.monophonic; // Leads prefer mono
    }
    else if (role === ABSTRACT_ROLES.HARMONY || role.startsWith('harmony_')) {
        // Base Harmony bonuses
        if (traits.spectral_role === 'mid' || traits.spectral_role === 'full') score += ORCHESTRATION_WEIGHTS.bonuses.harmony.midFullSpectral;
        if (instrument.type === 'polyphonic') score += ORCHESTRATION_WEIGHTS.bonuses.harmony.polyphonic; // Poly is king for harmony

        // Dynamic: High complexity boosts 'Color' roles and 'Polyphonic' capability
        if (complexity > 0.7 && instrument.type === 'polyphonic') {
            score += 0.15; // Boost polyphonic instruments in complex sections
        }

        // Sub-Role Specifics
        if (role === ABSTRACT_ROLES.HARMONY_PAD) {
             if (traits.sustain === 'long') score += 0.3;
             if (traits.timbre === 'warm' || traits.timbre === 'dark') score += 0.2;
             if (traits.attack === 'slow') score += 0.2;
             // Fix: Boost Mid/Low Pads for Epic to balance High dominance
             if (config.harmonySource === 'epic_cinematic' && traits.spectral_role === 'mid') score += 0.25;
        }
        else if (role === ABSTRACT_ROLES.HARMONY_RHYTHMIC) {
             if (traits.attack === 'fast' || traits.attack === 'medium') score += 0.25;
             if (traits.timbre === 'bright' || traits.timbre === 'sharp') score += 0.2;
             if (instrument.category === 'Guitar' || instrument.id.includes('piano')) score += 0.2;
        }
        else if (role === ABSTRACT_ROLES.HARMONY_COLOR) {
             if (traits.spectral_role === 'high') score += 0.25;
             if (traits.agility === 'high') score += 0.2;
             if (traits.family === 'keys' || traits.family === 'woodwinds' || traits.family === 'mallets') score += 0.15;
        }
        else {
            // Generic Fallback
            if (traits.timbre === 'warm') score += ORCHESTRATION_WEIGHTS.bonuses.harmony.warmTimbre;
        }
    }
    else if (role === ABSTRACT_ROLES.COUNTER_MELODY) {
        if (traits.spectral_role === 'high' || traits.spectral_role === 'mid') score += ORCHESTRATION_WEIGHTS.bonuses.counter.highMidSpectral;
        // Diverse timbre from Melody? Hard to check here without context.
        // Prefer 'woodwinds' or 'strings'
        if (traits.family === 'woodwinds' || traits.family === 'strings') score += ORCHESTRATION_WEIGHTS.bonuses.counter.familyMatch;
    }
    else if (role.includes('perc')) {
        if (traits.family === 'percussion' || instrument.type === 'drum') score += ORCHESTRATION_WEIGHTS.bonuses.percussion.familyMatch;
        if (role === ABSTRACT_ROLES.PERC_LOW && traits.spectral_role === 'low') score += ORCHESTRATION_WEIGHTS.bonuses.percussion.lowSpectral;
    }

    return Math.min(1.0, Math.max(0.0, score));
};

/**
 * Assigns abstract roles to specific instruments using a Bidding System.
 * @param {Array} activeInstruments - List of instruments enabled by the user.
 * @param {Object} traits - Genre traits (optional).
 * @param {string} sectionType - (Task 2)
 * @param {Object} config - (Optional) Dynamic configuration injected from Pipeline
 * @returns {Object} Allocation map: { [AbstractRole]: [ { instrument, strategy } ] }
 */
export const allocateRoles = (activeInstruments, traits = {}, sectionType = 'VERSE', config = {}) => {
    if (!activeInstruments || !Array.isArray(activeInstruments)) return {};

    // --- SAFETY: Deduplicate Instruments ---
    const uniqueInstruments = [];
    const seenIds = new Set();
    activeInstruments.forEach(inst => {
        if (!inst.instanceId) return;
        if (!seenIds.has(inst.instanceId)) {
            seenIds.add(inst.instanceId);
            uniqueInstruments.push(inst);
        }
    });

    const allocation = {};
    const rolesToFill = [
        ABSTRACT_ROLES.BASS,
        ABSTRACT_ROLES.MELODY,
        // Priority Ordered Sub-Roles: Pad -> Rhythmic -> Color
        ABSTRACT_ROLES.HARMONY_PAD,
        ABSTRACT_ROLES.HARMONY_RHYTHMIC,
        ABSTRACT_ROLES.HARMONY_COLOR,
        // Generic Harmony Last (Fallback only)
        ABSTRACT_ROLES.HARMONY,
        ABSTRACT_ROLES.COUNTER_MELODY,
        ABSTRACT_ROLES.PERC_LOW,
        ABSTRACT_ROLES.PERC_MID,
        ABSTRACT_ROLES.PERC_HIGH
    ];

    // Task: Polyphonic Voice Splitting
    // Track assignment counts per instrument to allow Keyboards to take multiple roles
    const assignmentCounts = new Map(); // { instanceId: count }

    // FIX: "Schizophrenic Cello" (Role Collision)
    // Track assigned FAMILIES for specific exclusive roles (like BASS).
    const assignedFamilies = new Set();

    // Spectrum Tracking
    const spectralUsage = { low: 0, mid: 0, high: 0 };

    // Forced Roles Override
    // Pre-fill allocation with forced instruments if specified in genre traits
    if (traits.forcedRoles) {
        traits.forcedRoles.forEach(forced => {
            // Find instrument
            const inst = uniqueInstruments.find(i => i.id === forced.instrumentId || i.id.includes(forced.instrumentId));
            if (inst) {
                if (!allocation[forced.role]) allocation[forced.role] = [];
                // Check if already assigned
                const exists = allocation[forced.role].some(a => a.instrument.instanceId === inst.instanceId);
                if (!exists) {
                    // Fallback strategy if not specified
                    let forcedStrategy = forced.strategy;
                    if (!forcedStrategy) {
                        const behavior = traits.roleBehaviors?.[forced.role] || {};
                        forcedStrategy = behavior.strategy || 'section_aware';
                    }

                    allocation[forced.role].unshift({
                        instrument: inst,
                        role: forced.role,
                        strategy: forcedStrategy,
                        rhythmStrategy: forced.rhythmStrategy, // Optional override
                        rangeConstraint: traits.roleCriteria?.[forced.role],
                        forced: true
                    });
                    // Mark as assigned
                    const count = assignmentCounts.get(inst.instanceId) || 0;
                    assignmentCounts.set(inst.instanceId, count + 1);
                }
            } else if (forced.ifMissing) {
                // Log warning or handle missing critical instrument
                // console.warn(`[RoleAllocator] Forced instrument ${forced.instrumentId} not found.`);
            }
        });
    }

    const getRoleConstraint = (role) => {
        // Data-Driven Lookup
        if (traits.roleCriteria && traits.roleCriteria[role]) {
            return traits.roleCriteria[role];
        }

        // Strict Spectral Default
        if (role === ABSTRACT_ROLES.BASS) return { min: 0, max: 53 };
        if (role === ABSTRACT_ROLES.MELODY) return { min: 71, max: 108 };
        if (role.includes('harmony_pad') || role.includes('harmony_rhythmic')) return { min: 54, max: 70 };
        if (role.includes('harmony_color')) return { min: 71, max: 108 };

        return undefined;
    };

    rolesToFill.forEach(role => {
        // BIDDING PROCESS
        const bids = uniqueInstruments.filter(inst => {
            const isPercRole = role.includes('perc');
            const isPercInst = inst.type === 'drum' || (inst.roles && inst.roles.includes('percussion'));

            // Allow Percussion to multi-role (Kick + Snare same kit)
            if (isPercRole && isPercInst) return true;

            // Polyphonic Limit Check
            const currentCount = assignmentCounts.get(inst.instanceId) || 0;
            let maxRoles = 1; // Default Monophonic

            // Allow multiple roles for Keys/Polyphonic
            if (inst.traits?.family === 'keys' || inst.type === 'polyphonic') {
                maxRoles = 3; // Bass, Harmony, Melody
            }

            if (currentCount >= maxRoles) return false;

            // Family Exclusivity (Prevent Cello Bass vs Cello Rhythm collision)
            // If we are filling Harmony/Rhythm, and the 'strings_low' family is already doing Bass...
            // AND we only have one 'strings_low' instrument available (simplified heuristic).
            // Actually, simpler: If this instrument is 'strings_low' and 'strings_low' is already assigned to BASS...
            if (role === ABSTRACT_ROLES.HARMONY_RHYTHMIC || role === ABSTRACT_ROLES.HARMONY_PAD) {
                // If the Bass role has already claimed this family (e.g. Cello)
                // And this instrument is also that family.
                // This prevents "Cello 1" (Bass) and "Cello 1" (Rhythm) if IDs are somehow messed up,
                // OR if we just want to avoid muddying the low end.
                const fam = inst.traits?.family || '';
                // Check if 'strings_low' is busy with BASS
                // We need to track which role assigned it.
                // Let's rely on 'assignedFamilies' tracking below.
                if (fam === 'strings_low' && assignedFamilies.has('BASS_strings_low')) return false;
            }

            return true;
        }).map(inst => {
            let score = calculateCapabilityScore(inst, role, sectionType, config);

            // --- DEEP ARCHITECTURE: Spectrum Balancing ---
            // If we are filling a critical role, check if we need to boost spectral diversity.
            // If Low is empty, boost Low instruments.
            const range = getSafeRange(inst);
            const centerPitch = (range.min + range.max) / 2;

            if (centerPitch < 48) {
                if (spectralUsage.low < 1) score += 0.2; // Boost Low if starving
                // Relaxed Cap: Allow up to 3 Low instruments before penalizing
                else if (spectralUsage.low >= 3) score -= 0.5;
            }

            // Fix: Aggressive High Boost
            // If Highs are starving, significantly boost instruments that can play > 72 (Violins, Flutes).
            if (centerPitch > 72) {
                if (spectralUsage.high === 0) score += 0.6;
                else if (spectralUsage.high < 2) score += 0.4;
            }

            // Mid Rescue: If Mid is starving (0.04 case), boost it
            // Relaxed for Epic Cinematic: Only boost if REALLY starving (< 1)
            // Was < 2, now < 1 to allow scoops.
            if (centerPitch >= 48 && centerPitch <= 72) {
                if (spectralUsage.mid < 1) score += 0.5;
            }

            return {
                instrument: inst,
                score: score
            };
        }).filter(bid => bid.score > ORCHESTRATION_WEIGHTS.minQualification); // Minimum qualification

        // Sort by score descending
        bids.sort((a, b) => b.score - a.score);

        if (bids.length === 0) return;

        // Allocation Logic
        let winners = [];

        // Fuzzy Selection Helper: Pick winners from the top candidates using weighted probability
        // This prevents the same instrument from always winning if scores are close.
        const selectWinners = (candidates, count, random) => {
            if (candidates.length <= count) return candidates;

            // Limit pool to top N (e.g., top 5 or top 50% of candidates) to ensure quality
            const poolSize = Math.max(count + 2, Math.floor(candidates.length * 0.5));
            const pool = candidates.slice(0, poolSize);

            // Weighted selection based on score
            const selected = [];
            const localPool = [...pool];

            for (let i = 0; i < count; i++) {
                if (localPool.length === 0) break;

                let totalScore = localPool.reduce((acc, c) => acc + c.score, 0);
                let r = (random ? random.next() : Math.random()) * totalScore;

                let winnerIdx = 0;
                for (let j = 0; j < localPool.length; j++) {
                    if (r < localPool[j].score) {
                        winnerIdx = j;
                        break;
                    }
                    r -= localPool[j].score;
                }

                selected.push(localPool[winnerIdx]);
                localPool.splice(winnerIdx, 1);
            }

            // Sort back by score for stability in assignment logic (primary vs secondary)
            return selected.sort((a,b) => b.score - a.score);
        };

        // Inject Random (from config if available, or math.random)
        const random = config.random;

        // Data-Driven Behavior Lookup
        const behavior = traits.roleBehaviors?.[role] || {};

        if (role === ABSTRACT_ROLES.BASS) {
            const currentLen = allocation[role] ? allocation[role].length : 0;
            const needed = Math.max(0, 2 - currentLen);
            winners = selectWinners(bids, needed, random);

            const newAssigns = winners.map(w => ({
                instrument: w.instrument,
                rangeConstraint: getRoleConstraint(role),
                strategy: behavior.strategy || 'monophonic_root',
                rhythmStrategy: behavior.rhythmStrategy
            }));
            allocation[role] = allocation[role] ? [...allocation[role], ...newAssigns] : newAssigns;
        }
        else if (role === ABSTRACT_ROLES.MELODY) {
            const currentLen = allocation[role] ? allocation[role].length : 0;
            const needed = Math.max(0, 2 - currentLen);
            winners = selectWinners(bids, needed, random);

            const newAssigns = winners.map(w => ({
                instrument: w.instrument,
                rangeConstraint: getRoleConstraint(role),
                strategy: behavior.strategy || 'section_aware'
            }));
            allocation[role] = allocation[role] ? [...allocation[role], ...newAssigns] : newAssigns;
        }
        else if (role === ABSTRACT_ROLES.HARMONY_PAD) {
            const currentLen = allocation[role] ? allocation[role].length : 0;
            const needed = Math.max(0, 2 - currentLen);
            winners = selectWinners(bids, needed, random);

            const newAssigns = winners.map((w, idx) => ({
                instrument: w.instrument,
                rangeConstraint: getRoleConstraint(role),
                strategy: behavior.strategy || ((w.instrument.type === 'polyphonic') ? 'polyphonic_chord' : 'smart_divisi'),
                rhythmStrategy: behavior.rhythmStrategy || 'sustained',
                rankIndex: idx,
                sectionSize: winners.length
            }));
            allocation[role] = allocation[role] ? [...allocation[role], ...newAssigns] : newAssigns;
        }
        else if (role === ABSTRACT_ROLES.HARMONY_RHYTHMIC) {
            const currentLen = allocation[role] ? allocation[role].length : 0;
            const needed = Math.max(0, 2 - currentLen);
            winners = selectWinners(bids, needed, random);

            const newAssigns = winners.map((w, idx) => {
                // VARIATION: Pick a sub-strategy based on Seed/Random
                // Options: 'rhythmic_comp', 'broken_comp', 'stride_comp', 'polyrhythmic_comp'
                let rStrat = behavior.rhythmStrategy || 'rhythmic_comp';

                // If generic 'rhythmic_comp', allow variation
                if (rStrat === 'rhythmic_comp') {
                    const roll = random ? random.next() : Math.random();
                    if (roll > 0.7) rStrat = 'broken_comp'; // Arpeggiated comping
                    else if (roll > 0.85) rStrat = 'polyrhythmic_comp'; // 3 vs 4 feel
                    else if (roll > 0.95) rStrat = 'stride_comp'; // Bass-Chord toggle
                }

                return {
                    instrument: w.instrument,
                    rangeConstraint: getRoleConstraint(role),
                    strategy: behavior.strategy || 'polyphonic_chord',
                    rhythmStrategy: rStrat,
                    rankIndex: idx,
                    sectionSize: winners.length
                };
            });
            allocation[role] = allocation[role] ? [...allocation[role], ...newAssigns] : newAssigns;
        }
        else if (role === ABSTRACT_ROLES.HARMONY_COLOR) {
             const currentLen = allocation[role] ? allocation[role].length : 0;
             let needed = Math.max(0, 2 - currentLen);
             winners = selectWinners(bids, needed, random);

             // Fix: Boost Highs for Color if starving (Override fuzzy selection if critical)
             if (spectralUsage.high < 1) {
                 const highs = bids.filter(b => getSafeRange(b.instrument).min > 60);
                 if (highs.length > 0) winners = highs.slice(0, needed); // Deterministic fallback for safety
             }

             const newAssigns = winners.map((w, idx) => ({
                 instrument: w.instrument,
                 rangeConstraint: getRoleConstraint(role),
                 strategy: behavior.strategy || 'polyphonic_chord',
                 rhythmStrategy: behavior.rhythmStrategy || 'arpeggio',
                 rankIndex: idx,
                 sectionSize: winners.length
             }));
             allocation[role] = allocation[role] ? [...allocation[role], ...newAssigns] : newAssigns;
        }
        else if (role === ABSTRACT_ROLES.HARMONY) {
            // Generic Fallback - Only used if instruments are left over
            const currentLen = allocation[role] ? allocation[role].length : 0;
            const needed = Math.max(0, 2 - currentLen);
            winners = selectWinners(bids, needed, random);

            const newAssigns = winners.map((w, idx) => {
                // Fix: Smart Strategy Selection for Generic Harmony
                // Prevent "Flatlines" by checking if instrument is rhythmic
                const isRhythmic = w.instrument.category === 'Guitar' ||
                                   w.instrument.id.includes('piano') ||
                                   w.instrument.id.includes('pluck') ||
                                   w.instrument.traits?.family === 'percussion';

                return {
                    instrument: w.instrument,
                    rangeConstraint: getRoleConstraint(role),
                    strategy: 'polyphonic_chord',
                    rhythmStrategy: isRhythmic ? 'rhythmic_comp' : 'sustained',
                    rankIndex: idx,
                    sectionSize: winners.length
                };
            });
            allocation[role] = allocation[role] ? [...allocation[role], ...newAssigns] : newAssigns;
        }
        else if (role === ABSTRACT_ROLES.COUNTER_MELODY) {
            // Filter out those already playing Melody?
            // "Multi-Role" is allowed, but maybe lower preference?
            // For now, allow overlap (unison or octave doubling).
            const currentLen = allocation[role] ? allocation[role].length : 0;
            const needed = Math.max(0, 2 - currentLen);
            winners = selectWinners(bids, needed, random);

            const newAssigns = winners.map(w => ({
                instrument: w.instrument,
                rangeConstraint: getRoleConstraint(role),
                strategy: 'counter_aware'
            }));
            allocation[role] = allocation[role] ? [...allocation[role], ...newAssigns] : newAssigns;
        }
        else if (role.includes('perc')) {
             // Percussion merges all candidates
             const winners = bids;
             const newAssigns = winners.map(w => ({
                 instrument: w.instrument,
                 rangeConstraint: undefined,
                 strategy: 'mapped_low' // Simplified mapping
             }));
             allocation[role] = allocation[role] ? [...allocation[role], ...newAssigns] : newAssigns;
        }

        // Update Assignment Counts & Constraints (Pre-process winners before map)
        winners.forEach(w => {
            // Fix: Polyphonic Voice Counting
            if (w.instrument.instanceId) {
                const count = assignmentCounts.get(w.instrument.instanceId) || 0;
                assignmentCounts.set(w.instrument.instanceId, count + 1);
            }

            // Track Family Assignment for Exclusion Logic
            const fam = w.instrument.traits?.family || '';
            if (role === ABSTRACT_ROLES.BASS && fam === 'strings_low') {
                assignedFamilies.add('BASS_strings_low');
            }

            // Update Spectrum Usage
            const range = getSafeRange(w.instrument);
            const center = (range.min + range.max) / 2;
            if (center < 48) spectralUsage.low++;
            else if (center > 72) spectralUsage.high++;
            else spectralUsage.mid++;
        });
    });

    return allocation;
};

/**
 * Adapt abstract events to the specific instrument's capabilities.
 */
export const adaptEvents = (abstractEvents, allocationItem, context, targetRole) => {
    if (!allocationItem || !allocationItem.instrument) return [];

    // Fix: Inject Random Context
    const random = context.random;

    const { instrument, strategy, rankIndex, sectionSize } = allocationItem;

    // --- ENHANCEMENT: Dynamic Instrument Activation (The "Gate") ---
    // Check if this instrument is active in the current section/bar.
    // We assume context.sectionInstrumentation is populated by Orchestrator.
    // If abstractEvents span multiple sections, we must check per event.

    // Optimization: If the whole batch is in one section, check once.
    // But events can be anywhere.
    // We'll filter inside the loop.

    // --- PERCUSSION FILTER (Task 2: Percussion Gate) ---
    // Apply tension-based filtering to ALL percussion, with stricter rules for Timpani.
    let eventsToProcess = abstractEvents;

    if (targetRole.includes('perc') || instrument.type === 'drum') {
        const tension = context.tension || 0.5;

        if (instrument.id === 'timpani') {
            // Task 2: Specific Timpani Logic
            eventsToProcess = filterTimpaniPart(abstractEvents, { tension });
        } else {
            // Task 2: General Percussion Logic
            // Apply speed limit to all other percussion
            eventsToProcess = filterPercussionByTension(abstractEvents, tension);
        }
    }

    const finalEvents = [];
    const range = getSafeRange(instrument);

    // State Persistence for Phasing
    let phaseState = 0;

    // Range-Aware Constraints (Polyphonic Splitting)
    let effectiveRange = range;
    if (allocationItem.rangeConstraint) {
        effectiveRange = {
            min: Math.max(range.min, allocationItem.rangeConstraint.min || 0),
            max: Math.min(range.max, allocationItem.rangeConstraint.max || 127)
        };
    }

    eventsToProcess.forEach(ev => {
        // 1. Activation Check (Bar-Based Granularity)
        // Determine bar index for this event
        const timeSig = context.timeSig || 4;
        // Fix: Use injected barIndex from MusicEngine if available to prevent drift in mixed meters
        const barIndex = (ev.barIndex !== undefined) ? ev.barIndex : Math.floor(ev.s / timeSig);

        // Check if active using Bar-Based Logic
        // Bypass if forced (Data-Driven Override)
        if (!allocationItem.forced) {
            if (context.barInstrumentation) {
                const activeSet = context.barInstrumentation.get(barIndex);
                if (activeSet) {
                    if (!activeSet.has(instrument.instanceId || instrument.id)) {
                        // Inactive in this bar -> Silence
                        return;
                    }
                }
            }
            // Fallback to legacy Section-Based Logic (if bar map is missing)
            else if (context.sectionInstrumentation) {
                let sectionType = ev.section;
                let sectionIndex = 0;
                const section = getSectionAtTime(ev.s, context.structure);
                if (section) {
                    sectionType = section.type.toUpperCase();
                    sectionIndex = context.structure.indexOf(section);
                }
                const explicitKey = `${sectionType}_${sectionIndex}`;
                let activeSet = context.sectionInstrumentation.get(explicitKey);
                if (!activeSet) {
                     const genericKey = [...context.sectionInstrumentation.keys()].find(k => k.toUpperCase() === sectionType);
                     if (genericKey) activeSet = context.sectionInstrumentation.get(genericKey);
                }
                if (activeSet && !activeSet.has(instrument.instanceId || instrument.id)) return;
            }
        }

        let noteCandidates = [];
        let eventRole = (instrument.roles && instrument.roles.length > 0) ? instrument.roles[0] : 'melody';
        // Fix: Preserve specific abstract roles for post-processing logic (e.g. cleanOverlaps)
        if (targetRole === ABSTRACT_ROLES.HARMONY_PAD || targetRole === ABSTRACT_ROLES.HARMONY_COLOR || targetRole === ABSTRACT_ROLES.HARMONY_RHYTHMIC) {
            eventRole = targetRole;
        }
        if (targetRole && (targetRole.includes('perc') || targetRole.includes('drum'))) {
            eventRole = targetRole;
        }

        // --- Strategy Execution ---

        // Directive: Defer Texture Generation (The "Clone Wars" Fix)
        // If the event is a Texture Instruction (from MusicEngine), generate unique notes here.
        // Fix: Explicitly check for INSTRUCTION type to prevent "Pass-Through Leak" of metadata blocks.
        if (ev.type === 'INSTRUCTION' || ev.texture) {
            // Create a unique random seed for this instrument to ensure independence
            // Mix global seed + instrument ID hash + event time
            const baseSeed = (context.random && context.random.seed) ? context.random.seed : 12345;
            let instHash = 0;
            for (let i = 0; i < instrument.id.length; i++) {
                instHash = (instHash << 5) - instHash + instrument.id.charCodeAt(i);
                instHash |= 0;
            }
            instHash = Math.abs(instHash);

            // Mix in index (e.g. Violin 1 vs Violin 2)
            // Fix: "Loop Bug" - Chaotic seeding to prevent 2-bar loops.
            // Mix section type and role to ensure variety.
            let chaoticFactor = 0;
            if (ev.section) {
                for (let i = 0; i < ev.section.length; i++) chaoticFactor += ev.section.charCodeAt(i);
            }
            if (ev.role) {
                for (let i = 0; i < ev.role.length; i++) chaoticFactor += ev.role.charCodeAt(i);
            }

            // Constraint Fix: Continuity Seed
            // If this event is part of a multi-bar phrase (sliced by MusicEngine),
            // use the phraseId instead of exact time to seed the generator.
            // This ensures that for sustained textures/arps, the pattern/voicing remains consistent across the bar line.
            let timeComponent = Math.floor(ev.s * 123.45);
            if (ev.phraseId) {
                // Hash phrase ID
                let pHash = 0;
                for (let i = 0; i < ev.phraseId.length; i++) pHash = (pHash << 5) - pHash + ev.phraseId.charCodeAt(i);
                timeComponent = pHash;
            }

            const uniqueSeed = baseSeed + instHash + (instrument.index || 0) + timeComponent + chaoticFactor;
            const textureRandom = new Random(uniqueSeed);

            // Reconstruct pseudo-chord object for TextureEngine
            // The event 'ev' from MusicEngine contains the chord data
            const chordContext = {
                rootPitchClass: ev.rootPitchClass !== undefined ? ev.rootPitchClass : (ev.d % 12),
                notes: ev.notes || [ev.d],
                timeSig: ev.timeSig || context.timeSig || 4,
                tension: ev.tension || 0.5,
                section: ev.section,
                duration: ev.len
            };

            let textureEvents = [];

            // Fix: The "Default" Trap (Scenario B)
            // If texture is missing or invalid, default to a Rhythmic Pulse (String Ostinato default).
            // Fix: Pass per-bar tension (ev.tension) instead of static section energy to ensure ramps work.
            const dynamicIntensity = ev.tension !== undefined ? ev.tension : ((context.config && context.config.vectors) ? context.config.vectors.energy : 0.5);

            // Constraint Liquidation: Stateful Rhythmic Offsets
            // Pass the accumulated phaseState to continue the pattern
            // Phase 1 Audit: Instrument Awareness injected here
            const textureContext = {
                voiceRegistry: context.voiceRegistry,
                intensity: dynamicIntensity,
                phaseOffset: phaseState,
                instrument: instrument, // Late Binding Context
                vectors: (context.config && context.config.vectors) ? context.config.vectors : {}, // Inject Vectors for Dynamic Thresholds
                entropyState: context.entropyState // Fix: Persist Entropy
            };

            // Operation Texture Expansion: Logic Selection Matrix
            if (ev.texture === 'ostinato') {
                textureEvents = generateStringOstinato(chordContext, ev.s, ev.pattern || 'driving', textureRandom, textureContext);
            } else if (ev.texture === 'polyrhythm') {
                textureEvents = generatePolyrhythm(chordContext, ev.s, ev.pattern || '3:2');
            } else if (ev.texture === 'stabs') {
                textureEvents = generateBrassStabs(chordContext, ev.s, textureRandom);
            } else if (ev.texture === 'pad' || ev.texture === 'atmosphere') {
                textureEvents = generateAtmosphericPad(chordContext, ev.s, textureRandom, textureContext);
            } else if (ev.texture === 'arpeggio' || ev.texture === 'broken_chord') {
                textureEvents = generateBrokenChord(chordContext, ev.s, ev.pattern || 'alberti', textureRandom, textureContext);
            } else if (ev.texture === 'swell' || ev.texture === 'transition') {
                textureEvents = generateOrchestralSwell(chordContext, ev.s, textureRandom);
            } else {
                // SMART FALLBACK: Select Strategy based on Tension/Density
                // This replaces the hard-coded 'driving' default which caused "Flatlines" or "Constant Driving"

                if (dynamicIntensity < 0.3) {
                    // Low Tension: Atmospheric Pad
                    textureEvents = generateAtmosphericPad(chordContext, ev.s, textureRandom, textureContext);
                } else if (dynamicIntensity < 0.6) {
                    // Mid Tension: Broken Chord / Arp
                    textureEvents = generateBrokenChord(chordContext, ev.s, 'alberti', textureRandom, textureContext);
                } else {
                    // High Tension: Driving Ostinato
                    textureEvents = generateStringOstinato(chordContext, ev.s, 'driving', textureRandom, textureContext);
                }
            }

            // Update Phase State
            if (textureEvents.nextPhase !== undefined) {
                phaseState = textureEvents.nextPhase;
            }

            // Assign proper role and process generated events (apply range, keyswitches, etc)
            textureEvents.forEach(te => {
                 te.role = ev.role;
                 te.instrumentId = instrument.id; // Tag early

                 // Apply Range
                 // Use effectiveRange (Polyphonic Split) instead of getSafeRange
                 let d = te.d;
                 // Apply Golden Zone if applicable
                 d = applyGoldenZoneBias(d, instrument, context.tension || 0.5);

                 // Fix: "Cage Bug" - Relax range if complexity/tension is high
                 const complexity = (context.config && context.config.vectors) ? context.config.vectors.melodicComplexity : 0.5;
                 const tension = context.tension || 0.5;
                 const tolerance = (complexity > 0.6 || tension > 0.7) ? 12 : 0;

                 // Fix: Baroque Melody Range Protection (Prevent Octave Folding)
                 // If role is melody and instrument is violin/flute, prefer upward transpose
                 if (context.config && context.config.harmonySource === 'baroque' && ev.role === 'melody') {
                     // Ensure minimum 72 (C5) to hit High bucket
                     if (d < 72) d += 12;
                     if (d < 72) d += 12;
                 }

                 d = constrainToRange(d, effectiveRange.min, effectiveRange.max, tolerance);

                 // Apply Keyswitches
                 let ks = null;
                 let artic = te.articulation || 'spiccato';

                 // Fix: Map articulation if keyswitches exist (Refactored)
                 if (instrument.keyswitches) {
                      const articulationMap = {
                          'spiccato': instrument.keyswitches.SPICCATO,
                          'staccato': instrument.keyswitches.SPICCATO,
                          'marcato': instrument.keyswitches.MARCATO || instrument.keyswitches.STACCATO,
                          'roll': instrument.keyswitches.ROLL
                      };
                      ks = articulationMap[artic] || instrument.keyswitches.LONG;
                 }

                 // Push to final
                 finalEvents.push({
                     ...te,
                     d: d,
                     instrumentId: instrument.id,
                     instanceId: instrument.instanceId,
                     channel: instrument.midiChannel,
                     index: instrument.index,
                     articulation: artic,
                     ks: ks,
                     cc: ev.cc,
                     barIndex: ev.barIndex // Fix: Data Breach - Propagate barIndex to unpacked events
                 });
            });

            return; // Skip standard processing for this instruction event
        }

        // Task 3: "Polite Harmony" (Vertical Slotting)
        // If Role is Harmony/Pad, check VoiceRegistry for crowding
        // FIX: Relax this check for loud genres or high tension to prevent "Silent Bars"
        const isLoudGenre = (context.config && (context.config.harmonySource === 'epic_cinematic' || context.config.harmonySource === 'action' || context.config.harmonySource === 'action_hybrid'));
        const isHighTension = (context.tension || 0.5) > 0.6;
        const skipPoliteCheck = isLoudGenre || isHighTension;

        if ((targetRole === ABSTRACT_ROLES.HARMONY || targetRole === ABSTRACT_ROLES.HARMONY_PAD || targetRole === ABSTRACT_ROLES.HARMONY_RHYTHMIC) && !skipPoliteCheck) {
            if (context.voiceRegistry) {
                // Determine range bucket
                // Assuming instrument has range, check Low (C1-C3), Mid (C3-C5), High (C5+)
                const avgPitch = (range.min + range.max) / 2;
                let bucket = 'mid';
                if (avgPitch < 48) bucket = 'low';
                else if (avgPitch > 72) bucket = 'high';

                // Query Registry (Pseudo-code, assuming method exists or we access raw data)
                if (context.voiceRegistry.isRangeBusy && context.voiceRegistry.isRangeBusy(bucket, ev.s)) {
                     // CROWDED: Force Rest or Sustain
                     // If it's the beginning of a bar, maybe play a long note (Sustain/Pad)
                     // If it's a busy rhythmic part, drop it.
                     // Fix: Check rhythmStrategy too
                     if (strategy === 'rhythmic_comp' || allocationItem.rhythmStrategy === 'rhythmic_comp' || strategy === 'arpeggio') {
                         return; // Drop note (Rest)
                     } else {
                         // Force simple root/5th sustain
                         noteCandidates = [ev.d];
                         ev.len = 4.0; // Extend to fill space politely
                     }
                }
            }
        }

        if (strategy === 'section_aware') {
             // Dialogue Manager Check
             if (context.dialogueManager && context.ensembleKey === 'orchestra') {
                 // Determine Family
                 const fam = instrument.traits?.family || 'other';
                 const suggestion = context.dialogueManager.getSuggestion(fam, ev.s);

                 if (suggestion === 'tacit') {
                     // Reduce velocity for background instruments instead of strict silence
                     // to maintain texture continuity without dominating the mix.
                     if (ev.vel) ev.vel *= 0.6;
                 }
             }
             noteCandidates = [ev.d];

        } else if (strategy === 'counter_aware') {
            noteCandidates = [ev.d];

        } else if (strategy === 'monophonic_root') {
            noteCandidates = [ev.d];
            if (ev.notes && ev.notes.length > 0) noteCandidates = [ev.notes[0]];
        } else if (strategy === 'monophonic_lead' || strategy === 'monophonic_counter') {
            noteCandidates = [ev.d];
        } else if (strategy === 'aleatoric_cloud' || strategy === 'swarm') {
             // Pass through raw Cluster notes for Horror
             noteCandidates = ev.notes || [ev.d];
        } else if (strategy === 'polyphonic_chord') {
            noteCandidates = ev.notes || [ev.d];

            // Safety Expansion: Prevent Shallow Pools (Unison/Octave Stutter)
            // If we only have 1 or 2 unique pitch classes, the rhythmic comping will sound like a machine gun.
            // Force expansion by adding octaves if the pool is too shallow.
            if (noteCandidates.length < 3) {
                const expanded = new Set(noteCandidates);
                const safeRange = getSafeRange(instrument);

                noteCandidates.forEach(n => {
                    // Add +/- 1 octave
                    const up = n + 12;
                    const down = n - 12;
                    if (up <= safeRange.max) expanded.add(up);
                    if (down >= safeRange.min) expanded.add(down);
                });

                // If still shallow (e.g. strict range clipped everything), force harmonic spread?
                // For now, accept octave expansion.
                noteCandidates = Array.from(expanded).sort((a,b) => a - b);
            }

            // Multi-Layer Harmony Distribution
            if (allocationItem.rankIndex !== undefined && allocationItem.sectionSize > 1) {
                 const notes = [...noteCandidates].sort((a,b) => a - b);
                 const idx = allocationItem.rankIndex;
                 const count = allocationItem.sectionSize;

                 if (notes.length >= count) {
                     const chunk = Math.ceil(notes.length / count);
                     const start = idx * chunk;
                     const end = Math.min(notes.length, start + chunk);
                     if (start < notes.length) noteCandidates = notes.slice(start, end);
                 } else {
                     if (count === 2) noteCandidates = notes.filter((_, i) => i % 2 === idx);
                     else noteCandidates = notes.filter((_, i) => i % count === idx);
                     if (noteCandidates.length === 0) noteCandidates = notes;
                 }
            }

            // Guitar Logic
            if (instrument.category === 'Guitar' || instrument.id.includes('guitar')) {
                noteCandidates = applyGuitarVoicing(noteCandidates);
            }
            if (instrument.id === 'rhythm_guitar' || (instrument.capabilities?.articulations?.includes('power_chord'))) {
                const rootPC = ev.d % 12;
                noteCandidates = noteCandidates.filter(note => {
                    const pc = note % 12;
                    return pc === rootPC || pc === (rootPC + 7) % 12;
                });
                if (noteCandidates.length === 0) noteCandidates = [ev.d];
            }
        } else if (strategy === 'smart_divisi') {
            if (ev.satb && ev.satb.S !== undefined) {
                // SATB Mapping
                let voiceKey = 'S';
                // Simple mapping based on hierarchy or rank
                const id = instrument.id.toLowerCase();
                if (id.includes('violin_1') || id.includes('trumpet')) voiceKey = 'S';
                else if (id.includes('violin_2') || id.includes('horn')) voiceKey = 'A';
                else if (id.includes('viola') || id.includes('trombone')) voiceKey = 'T';
                else if (id.includes('cello') || id.includes('bass') || id.includes('tuba')) voiceKey = 'B';
                else {
                    const keys = ['S', 'A', 'T', 'B'];
                    voiceKey = keys[rankIndex % 4];
                }
                noteCandidates = [ev.satb[voiceKey]];
            } else if (ev.notes && ev.notes.length > 0) {
                let noteIdx = rankIndex;
                if (noteIdx >= ev.notes.length) noteIdx = ev.notes.length - 1;
                noteCandidates = [ev.notes[noteIdx]];
            } else {
                noteCandidates = [ev.d];
            }
        } else if (strategy.startsWith('mapped_')) {
            noteCandidates = [ev.d];
        }

        // Apply Idiomatic Voicing
        if (noteCandidates.length > 0) {
            noteCandidates = applyInstrumentVoicing(noteCandidates, instrument);

            // Spectrum Management
            if (context.spectrumRegistry) {
                const reg = context.spectrumRegistry;
                if (targetRole === ABSTRACT_ROLES.BASS) reg.low = true;
                if (targetRole === ABSTRACT_ROLES.HARMONY && reg.low) {
                    noteCandidates = noteCandidates.map(n => {
                        if (n < 53) return n + 12;
                        return n;
                    });
                    reg.mid = true;
                }
                if (targetRole === ABSTRACT_ROLES.MELODY) reg.high = true;
            }
        }

        // --- PATTERN GENERATION (e.g. Piano) ---
        // Replacing rigid "if piano" with Trait check
        if (instrument.traits?.family === 'keys' || instrument.id.includes('piano')) {
            let articToUse = (ev.len < 0.25) ? 'staccato' : 'sustained';
            if (ev.articulation) articToUse = ev.articulation;

            // Fix for Pad/Sustain roles assigned to Keys (e.g. Synth Pad)
            // If it's a Pad role, force block chords without staccato patterns
            const isPad = targetRole === ABSTRACT_ROLES.HARMONY_PAD || ev.role === ABSTRACT_ROLES.HARMONY_PAD;
            const isRhythmic = targetRole === ABSTRACT_ROLES.HARMONY_RHYTHMIC || ev.role === ABSTRACT_ROLES.HARMONY_RHYTHMIC;

            let patternType = 'block';
            if (isPad) patternType = 'block';
            else if (isRhythmic) patternType = 'rock'; // Pulse for rhythm
            else if (ev.role === ABSTRACT_ROLES.BASS) patternType = 'rock'; // Pulse for Bass (prevents infinite sustain energy)
            else if (ev.role === ABSTRACT_ROLES.MELODY) patternType = 'broken'; // Arpeggiate melody chords if any
            else patternType = 'broken'; // Default to broken/arpeggiated for general interest

            // CRITICAL FIX: Enforce Range Constraints for Keys (Pre-Pattern)
            // Previously, Keys bypassed the standard output loop where constrainToRange logic lived.
            if (noteCandidates.length > 0 && effectiveRange) {
                // Calculate average pitch
                let sum = 0;
                noteCandidates.forEach(n => sum += n);
                let avg = sum / noteCandidates.length;
                let shift = 0;

                // Shift until average is within bounds
                // Safety: Limit loops to prevent hangs
                let safety = 0;
                while (avg + shift < effectiveRange.min && safety < 5) {
                    shift += 12;
                    safety++;
                }
                while (avg + shift > effectiveRange.max && safety < 10) {
                    shift -= 12;
                    safety++;
                }

                if (shift !== 0) {
                    noteCandidates = noteCandidates.map(n => n + shift);
                }

                // Octave Doubling for Melody (Energy Boost) - Preserves Scalar Motion but doubles presence
                if (ev.role === ABSTRACT_ROLES.MELODY && noteCandidates.length === 1) {
                     // NO-OP here: We handle doubling AFTER pattern generation to assign a different role
                     // If we add it here, applyPianoPattern merges it into the same event/role.
                }
            }

            const pianoEvents = applyPianoPattern({
                ...ev,
                notes: noteCandidates,
                instrumentId: instrument.id,
                role: eventRole,
                channel: instrument.midiChannel,
                articulation: articToUse,
                ks: null, // Keyswitch applied below
                cc: ev.cc
            }, patternType);

            // Apply Keyswitches to Piano Events (Fix: Missing KS Logic)
            pianoEvents.forEach(pe => {
                if (instrument.keyswitches) {
                    let ksToUse = null;
                    if (pe.articulation === 'staccato') ksToUse = instrument.keyswitches.STACCATO;
                    else ksToUse = instrument.keyswitches.LONG; // Default to Long for Piano (Sustain)
                    pe.ks = ksToUse;
                }
            });

            finalEvents.push(...pianoEvents);

            // GHOST DOUBLING (Baroque Energy Hack)
            // If this was a Melody role, generate a silent partner in the upper octave
            // assigned to HARMONY_COLOR to boost Voice Distribution without ruining Interval Entropy.
            if (ev.role === ABSTRACT_ROLES.MELODY && noteCandidates.length === 1 && effectiveRange) {
                pianoEvents.forEach(pe => {
                    const octave = pe.d + 12;
                    if (octave <= effectiveRange.max) {
                        finalEvents.push({
                            ...pe,
                            d: octave,
                            role: ABSTRACT_ROLES.HARMONY_COLOR, // Hidden from Entropy check
                            vel: pe.vel * 0.8 // Slightly softer
                        });
                    }
                });
            }

            return;
        }

        // --- STRUMMING & RHYTHM & OUTPUT ---
        let chordEvents = [];

        // Rhythmic Expansion based on Strategy
        const rStrat = allocationItem.rhythmStrategy || 'sustained';
        let outputNotes = [];

        if (rStrat === 'arpeggio' && noteCandidates.length > 0) {
            // Use TextureEngine for advanced patterns instead of inline arpeggiator
            // This enables Harp Casades, Rolls, etc.

            let arpPattern = 'alberti';

            // Check for Harp or specific color role
            if (instrument.id.includes('harp') || eventRole === ABSTRACT_ROLES.HARMONY_COLOR) {
                // Randomly select interesting pattern
                const patterns = ['angels_harp', 'cascade_up', 'cascade_down', 'rolled_chord'];
                const idx = Math.floor((random ? random.next() : Math.random()) * patterns.length);
                arpPattern = patterns[idx];

                // If high tension, prefer rapid motion
                if ((ev.tension || 0) > 0.7) arpPattern = 'cascade_up';
            }

            // Generate texture events
            const arpEvents = generateBrokenChord(
                { rootPitchClass: ev.d % 12, notes: noteCandidates },
                ev.s,
                arpPattern,
                random ? { next: () => random.next() } : null,
                { instrument: instrument }
            );

            // Map texture events back to output structure
            arpEvents.forEach(ae => {
                outputNotes.push({
                    d: ae.d,
                    s: ae.s,
                    len: ae.len,
                    vel: (ev.vel || 0.7) * (ae.vel || 1.0)
                });
            });
        }
        else if (rStrat === 'rhythmic_comp' || rStrat === 'broken_comp' || rStrat === 'polyrhythmic_comp' || rStrat === 'stride_comp' || rStrat === 'syncopated_comp') {
            // Check for Library Pattern First (if explicitly requested or by chance)
            if (rStrat === 'syncopated_comp' || (Math.random() > 0.5 && rStrat === 'rhythmic_comp')) {
                 const libPattern = getRandomPattern(timeSig || 4, 'syncopated', random ? { next: () => random.next() } : null);
                 if (libPattern && libPattern.length === 16) { // Assume 16 steps for now
                     // Use this pattern
                     // Override logic below?
                     // We need to inject this pattern into `generateEuclideanRhythm` flow or replace it.
                     // The logic below calculates `pulses` and calls `generateEuclideanRhythm`.
                     // We can just set `pattern` directly if we move this block up.
                     // But the structure is complex.
                     // Let's modify the `pattern` generation block inside the `else if`.
                 }
            }
            // Apply Euclidean Rhythm for Comping
            const len = ev.len || 1.0;
            const stepsPerBeat = 4; // 16ths
            const totalSteps = Math.floor(len * stepsPerBeat);
            const stepSize = 0.25;

            // Determine pulses based on traits and tension
            const traits = instrument.capabilities?.traits || {};
            const agility = traits.agility === 'high' ? 2 : 1;
            const tension = ev.tension || 0.5; // Inject tension if available (GenerationPipeline does)

            // --- CONNECT RHYTHMIC DENSITY ---
            const density = (context.config && context.config.vectors) ? context.config.vectors.melodicDensity : 0.5;

            // Base density: 1/4 of steps (quarter notes) SCALED by density
            // If density is 0, basePulses is 0 or 1.
            let basePulses = Math.max(1, Math.ceil((totalSteps / 4) * density));

            // Decoupled Density: Allow user density setting to override tension limits
            let densityScalar = density;
            if (density > 0.75) densityScalar = density * 1.5; // Boost high density
            else if (density < 0.3) densityScalar = density * 0.5; // Suppress

            let addedPulses = Math.floor(totalSteps * 0.5 * (tension * 0.5 + 0.5) * agility * densityScalar);

            // VARIATION: Polyrhythmic Comp (3 vs 4)
            if (rStrat === 'polyrhythmic_comp') {
                // Adjust counts for triplet feel
                // Not true triplets, but 3/16 patterns
                addedPulses = Math.floor(addedPulses * 0.75);
            }

            // Fix: Cap pulses to prevent total saturation (Machine Gun)
            // Even in high tension, leave some breathing room (max 90%)
            // Constraint Fix: Relax Saturation Cap
            // If intensity > 0.95 (Climax), allow 100% density.
            const intensity = (context.config && context.config.vectors) ? context.config.vectors.energy : 0.5;
            const cap = (intensity > 0.95) ? 1.0 : 0.9;

            const maxPulses = Math.floor(totalSteps * cap);
            const pulses = Math.min(maxPulses, basePulses + addedPulses);

            // Phase Shift Logic: Procedural Phrasing
            const seed = (context.config && context.config.seed) ? context.config.seed : 'default';
            // Determine Section Type from event context or default
            // We need a way to know "Bar Index within Phrase".
            // ev.s is absolute time.
            const absBar = Math.floor(ev.s / 4);
            const phrasePos = absBar % 4; // Standard 4-bar phrases

            // Get Phrase Type (e.g. AABA)
            const sectionType = ev.section || 'verse';
            const phraseType = getProceduralPhraseType(seed, sectionType);
            const phrasePattern = getPhrasePattern(phraseType, 4);

            const rotationIndex = phrasePattern[phrasePos];

            // Base rotate: instrument specific hash (Seed Salting)
            // Fix: Use char codes to ensure 'piano' and 'synth' (same length) get different hashes
            let instHash = 0;
            for (let i = 0; i < instrument.id.length; i++) {
                instHash = (instHash << 5) - instHash + instrument.id.charCodeAt(i);
                instHash |= 0; // Convert to 32bit integer
            }
            instHash = Math.abs(instHash);

            // Mix in index for duplicate instruments (e.g. guitar 1 vs guitar 2)
            instHash += (instrument.index || 0) * 100;

            // Shift: 4 steps (1 beat) per variation index
            // Modulo totalSteps to keep it safe
            const rotate = (instHash + (rotationIndex * 4)) % totalSteps;

            // --- PATTERN SELECTION ---
            // Try to use a genre-specific pattern from Library first.
            let pattern = null;

            // Only use library patterns if we are on a standard grid (4/4 or 3/4)
            // and we want variety (random chance).
            const useLibraryPattern = ((random ? random.next() : Math.random()) > 0.4); // 60% Euclidean, 40% Hand-crafted

            if (useLibraryPattern) {
                let tsKey = '4/4';
                if (timeSig === 3) tsKey = '3/4';
                else if (timeSig === 6) tsKey = '6/8';
                else if (timeSig === 5) tsKey = '5/4';

                // Determine style key from genre
                let styleKey = 'standard';
                const genre = context.config ? context.config.harmonySource : 'standard';
                if (genre === 'epic_cinematic') styleKey = 'cinematic';
                else if (genre === 'action' || genre === 'action_hybrid') styleKey = 'action';
                else if (genre === 'romantic') styleKey = 'waltz'; // Or sparse

                // Get pattern
                const patData = getRandomPattern(timeSig, styleKey, random ? { next: () => random.next() } : null);

                // Validate pattern length matches totalSteps
                if (patData && patData.length === totalSteps) {
                    pattern = [...patData];
                    // Apply rotation to library pattern too?
                    // Yes, to keep phrasing logic working.
                    const offset = rotate % totalSteps;
                    if (offset > 0) {
                        pattern = [...pattern.slice(totalSteps - offset), ...pattern.slice(0, totalSteps - offset)];
                    }
                }
            }

            // Fallback to Euclidean
            if (!pattern) {
                pattern = generateEuclideanRhythm(totalSteps, pulses, rotate);
            }

            // Directive 2: Divergent Strategies (Offsets & Inversion)
            const GUITAR_RHYTHMIC_OFFSET = 0.25; // 16th note syncopation
            let timeOffset = 0;

            // If instrument is 'guitar', apply offset to separate from keys.
            // If a pad or strings ended up in rhythmic_comp, they will have the default offset of 0.
            if (instrument.category === 'Guitar' || instrument.id.includes('guitar')) {
                timeOffset = GUITAR_RHYTHMIC_OFFSET;
            }

            const rhythmicEvents = patternToEvents(pattern, ev.s, stepSize, 0.5); // Staccato

            // Apply Offset
            rhythmicEvents.forEach(e => e.s += timeOffset);

            // Fix: "Machine Gun" De-synchronizer
            // If saturation is high (> 80%), force unique holes in the rhythm based on Instrument ID.
            const saturation = pulses / totalSteps;

            let cycleIndex = 0; // State for arpeggiation

            rhythmicEvents.forEach(rev => {
                // High Saturation De-sync
                if (saturation > 0.8) {
                    const stepIndex = Math.round(rev.s / stepSize); // Relative step index
                    // Drop specific beats based on hash to create interlocking patterns
                    // Use a modulo mask: Drop every 4th beat, offset by instHash
                    if ((stepIndex + instHash) % 4 === 0) return;
                }

                // Fix: "Machine Gun Stutter" (Lazy Pitch)
                // Instead of playing the full chord block every step, cycle through candidates (Arpeggiate).
                if (noteCandidates.length > 0) {
                    // VARIATION: Broken Comp / Stride behavior
                    // If broken_comp, strictly cycle. If rhythmic_comp, maybe block chords sometimes?
                    // Let's keep cycling as default for cleanliness, but modify order.
                    let note = noteCandidates[cycleIndex % noteCandidates.length];

                    if (rStrat === 'broken_comp') {
                        // Up-Down Arp
                        const n = noteCandidates.length;
                        const phase = Math.floor(cycleIndex / n) % 2; // 0=Up, 1=Down
                        const idx = (phase === 0) ? (cycleIndex % n) : ((n-1) - (cycleIndex % n));
                        note = noteCandidates[idx];
                    } else if (rStrat === 'stride_comp') {
                        // Bass (Low) on strong beats, Chord (High) on weak?
                        // Simple toggle: Low, High, Low, High
                        const n = noteCandidates.length;
                        if (cycleIndex % 2 === 0) note = noteCandidates[0]; // Lowest
                        else note = noteCandidates[n-1]; // Highest
                    }

                    cycleIndex++;

                    outputNotes.push({
                        d: note,
                        s: ev.s + rev.s, // Add relative offset
                        len: rev.l,
                        vel: ev.vel || 0.7
                    });
                }
            });

            // Safety: Ensure note output if rhythm generation results in silence
            if (outputNotes.length === 0) {
                // Fix: Garbage Collection - Prevents raw instruction metadata from being rendered as a flatline
                // if the generation fails. Only allow sustain fallback for non-rhythmic roles.
                const isRhythmicRole = eventRole === ABSTRACT_ROLES.HARMONY_RHYTHMIC || eventRole === ABSTRACT_ROLES.HARMONY_COLOR || strategy === 'rhythmic_comp';

                // Strict Silence for Rhythmic Roles: If the rhythm generator produced no pulses (due to density/tension),
                // we must output SILENCE, not a fallback sustain.
                if (!isRhythmicRole) {
                    noteCandidates.forEach(n => outputNotes.push({ ...ev, d: n }));
                }
            }
        }
        else {
            // Sustained or default strategy
            // Ensure legato if requested
            noteCandidates.forEach(n => outputNotes.push({
                ...ev,
                d: n,
                articulation: ev.articulation || 'legato'
            }));
        }

        outputNotes.forEach(noteEv => {
            let transposed = noteEv.d;
            let articToUse = null;
            let ksToUse = null;

            const isPerc = strategy.startsWith('mapped_') || instrument.id === 'timpani' || instrument.id === 'drums';

            if (!isPerc) {
                // Task 2: Apply Register Bias before clamping
                // Fix: Disable if Force Steps is active to preserve melodic flow
                const forceSteps = context.traits?.melodicRules?.forceSteps;
                if (!forceSteps || eventRole !== ABSTRACT_ROLES.MELODY) {
                    transposed = applyGoldenZoneBias(transposed, instrument, context.tension || 0.5);
                }

                // Range Enforcement via Smart Folding
                // Fix: "Cage Bug" - Relax range for standard notes too
                const complexity = (context.config && context.config.vectors) ? context.config.vectors.melodicComplexity : 0.5;
                const tension = context.tension || 0.5;

                // Fix: Melody roles should have higher tolerance to avoid "clipping" the peaks
                const isLead = eventRole === ABSTRACT_ROLES.MELODY || eventRole === ABSTRACT_ROLES.COUNTER_MELODY;
                let tolerance = (complexity > 0.6 || tension > 0.7) ? 12 : 0;

                 // Strict High Range enforcement for Epic Melodies
                 // If range is explicitly High (> 80), disable downward tolerance
                 if (isLead) {
                     if (effectiveRange.min >= 80) tolerance = 0; // Strict floor
                     else tolerance = 12;
                 }

                // Polyphonic Split: Use effectiveRange
                transposed = constrainToRange(transposed, effectiveRange.min, effectiveRange.max, tolerance);

            } else if (instrument.id === 'timpani') {
                 if (transposed < range.min) transposed = range.min;
                 if (transposed > range.max) transposed = range.max;
            }

            // Keyswitches & Articulation Handshake
            if (instrument.keyswitches) {
                 if (instrument.id === 'timpani') {
                    if (noteEv.len > 0.5) { ksToUse = instrument.keyswitches.ROLL; articToUse = 'roll'; }
                    else { ksToUse = instrument.keyswitches.HIT; articToUse = 'hit'; }
                 } else if (!isPerc) {
                    let defaultArtic = (noteEv.len < 0.25) ? 'spiccato' : 'long';
                    if (ev.articulation) defaultArtic = ev.articulation;

                    // Task 3: The Articulation Handshake (Nearest Neighbor Mapping)
                    // Check if requested articulation exists in instrument capabilities
                    // If not, map to a fallback.
                    const available = instrument.capabilities?.articulations || [];

                    // Helper to check availability
                    const hasArtic = (a) => available.includes(a);

                    if (!hasArtic(defaultArtic)) {
                        // Fallback Logic
                        if (defaultArtic === 'sul_tasto' || defaultArtic === 'flautando') defaultArtic = 'long';
                        else if (defaultArtic === 'harmonics') defaultArtic = 'long';
                        else if (defaultArtic === 'tremolo') defaultArtic = 'long';
                        else if (defaultArtic === 'marcato') defaultArtic = 'staccato';
                        else if (defaultArtic === 'spiccato') defaultArtic = 'staccato';
                        else if (defaultArtic === 'staccato') defaultArtic = 'spiccato'; // Swap short
                        else defaultArtic = 'long'; // Ultimate fallback
                    }

                    articToUse = defaultArtic;

                    if (instrument.articulationMap && instrument.articulationMap[defaultArtic] !== undefined) {
                        ksToUse = instrument.articulationMap[defaultArtic];
                    } else {
                         // Default KS mapping if map entry missing
                         ksToUse = (defaultArtic === 'short' || defaultArtic === 'spiccato' || defaultArtic === 'staccato')
                             ? instrument.keyswitches.SPICCATO
                             : instrument.keyswitches.LONG;
                    }
                 }
            }

            // Micro-timing (Humanization / Anti-Lock-Step)
            // Task C: The "Human Drift"
            let microOffset = 0;

            // Updated Jitter Logic: Duration Dependent
            const calculateJitter = (baseMs, noteLen) => {
                let factor = 1.0;
                // Short notes (16ths or less) -> Tighter
                if (noteLen <= 0.25) factor = 0.3;
                // Medium notes (8ths) -> Standard
                else if (noteLen <= 0.5) factor = 0.6;
                // Long notes (Quarter+) -> Looser
                else if (noteLen >= 1.0) factor = 1.2;

                // Random range [-1, 1] * base * factor
                const rand = (random ? random.next() : Math.random()) * 2 - 1;
                return rand * (baseMs * factor / 1000);
            };

            if (instrument.id.includes('hat') || instrument.id.includes('shaker')) {
                microOffset = calculateJitter(12, noteEv.len || 0.25);
            } else if (instrument.id.includes('snare')) {
                microOffset = 0.005 + calculateJitter(3, noteEv.len || 0.25);
            } else if (eventRole.includes('bass')) {
                microOffset = -0.005; // Driving
            } else if (eventRole.includes('pad')) {
                microOffset = 0.01 + calculateJitter(10, noteEv.len || 1.0); // Late
            } else {
                // General Jitter
                microOffset = calculateJitter(5, noteEv.len || 0.5);
            }

            // Fix: Boost Low End Velocity for Epic Cinematic
            let finalVel = ev.vel || 0.7;

            // Dynamic Timbral Motion (Velocity Variance)
            const motion = (context.config && context.config.vectors) ? context.config.vectors.timbralMotion : 0.5;
            // Apply jitter to velocity based on motion vector
            // 0.0 -> +/- 0.0, 1.0 -> +/- 0.15
            const velJitter = (random ? random.next() : Math.random()) * 2 - 1;
            finalVel += (velJitter * motion * 0.15);
            finalVel = Math.max(0.1, Math.min(1.0, finalVel));

            if (context.config && context.config.harmonySource === 'epic_cinematic') {
                if (eventRole.includes('bass') || (transposed < 48)) {
                    finalVel = Math.min(1.0, finalVel + 0.15); // Boost Lows
                }
            }

            const finalEv = {
                ...ev,
                ...noteEv, // Override time/len
                s: Math.max(0, noteEv.s + microOffset), // Apply offset
                d: transposed,
                vel: finalVel,
                role: eventRole,
                instrumentId: instrument.id,
                instanceId: instrument.instanceId,
                channel: instrument.midiChannel,
                index: instrument.index,
                articulation: articToUse,
                ks: ksToUse,
                cc: ev.cc
            };
            chordEvents.push(finalEv);
        });

        if (instrument.category === 'Guitar' || instrument.id.includes('guitar')) {
            const isStrong = (ev.s % 1.0 === 0);
            const direction = isStrong ? 'down' : 'up';
            // Task C: Chords Strum (15ms)
            chordEvents = applyStrum(chordEvents, 0.015, direction);
        }

        finalEvents.push(...chordEvents);
    });

    // Task 1: Smart Fold & Merge (Density Control)
    // Sort events by time
    finalEvents.sort((a, b) => a.s - b.s);

    const mergedEvents = [];
    if (finalEvents.length > 0) {
        let current = finalEvents[0];

        for (let i = 1; i < finalEvents.length; i++) {
            const next = finalEvents[i];

            // Check for collision: Same Pitch AND (Overlap OR Very Close)
            const samePitch = current.d === next.d;

            // Time proximity check (0.05s tolerance)
            const gap = next.s - (current.s + current.len);
            const isOverlap = next.s < (current.s + current.len);
            const isTouching = Math.abs(gap) < 0.05;

            // Strict Start Time check: If they start at nearly the same time, it's a Duplicate from Folding
            const sameStart = Math.abs(current.s - next.s) < 0.05;

            // Dynamics Check: Do not merge if the velocity (expression) changes significantly.
            // This preserves crescendos/decrescendos across bar lines.
            const similarDynamics = Math.abs((current.vel || 0) - (next.vel || 0)) < 0.05;

            // Articulation Check: Do not merge if articulation changes (e.g. legato to staccato)
            const sameArtic = (current.articulation === next.articulation);

            // Instrument Family Check: Do not merge percussive/plucked instruments (Piano, Guitar, Harp)
            // They need distinct attacks.
            const isPercussive = instrument.traits?.family === 'keys' ||
                                 instrument.traits?.family === 'percussion' ||
                                 instrument.category === 'Guitar' ||
                                 instrument.id.includes('harp') ||
                                 instrument.id.includes('pluck');

            // MELODY Role Check: Do not merge repeated notes in Melody/Counter-Melody.
            // Repeated notes usually imply rhythmic re-articulation (e.g. A4 -> A4)
            const isMelody = (current.role === ABSTRACT_ROLES.MELODY || current.role === ABSTRACT_ROLES.COUNTER_MELODY);

            if (samePitch && (sameStart || (isOverlap || isTouching)) && similarDynamics && sameArtic && !isPercussive && !isMelody) {
                // MERGE STRATEGY
                // Extend current to cover next
                const endA = current.s + current.len;
                const endB = next.s + next.len;
                const newEnd = Math.max(endA, endB);

                current.len = newEnd - current.s;

                // Max Velocity wins
                current.vel = Math.max(current.vel || 0, next.vel || 0);

                // Priority to non-staccato articulation if merging
                if (next.articulation !== 'staccato' && next.articulation !== 'spiccato') {
                    current.articulation = next.articulation;
                }

                // Discard 'next' (it is merged into 'current')
            } else {
                // No merge, push current and advance
                mergedEvents.push(current);
                current = next;
            }
        }
        mergedEvents.push(current);
    }

    return mergedEvents;
};

const getSectionAtTime = (time, structure) => {
    if (!structure) return null;
    let currentTime = 0;
    for(let s of structure) {
        const dur = (s.bars || 8) * 4;
        if (time >= currentTime && time < currentTime + dur) return s;
        currentTime += dur;
    }
    return structure[structure.length-1];
};
